import GlobalStyles from './styles/GlobalStyles';
import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ApolloProvider } from '@apollo/client';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';
import { Integrations } from '@sentry/tracing';
import { Replay } from '@sentry/replay';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
import { ErrorPage } from './views';
import client from './setupApollo';
import { AppProvider } from './context/NotificationContext';
import { ModalsProvider } from './context/ModalsContext';
import { NavigationProvider } from './context/NavigationContext';
import { PendingSaveProvider } from 'context/PendingSaveContext';
import {
  hideIntercom,
  initIntercom,
  showIntercomHome,
} from './utils/intercomHelper';
import AppRouter from 'routing/AppRouter';
import { GetStartedProvider } from 'context/GetStartedContext';
import './styles/globals.css';

import '@symplbe/sympl-components/dist/style.css';
import { UserProvider } from 'context/UserContext';
import { PermissionProvider } from 'context/PermissionContext';

import { PostHogProvider } from 'posthog-js/react';
import { Sparkle } from '@phosphor-icons/react';
import 'twin.macro';

if (!import.meta.env.DEV) {
  const sentryEnv = window.location.hostname.includes('sympltest')
    ? 'qa'
    : 'production';

  Sentry.init({
    dsn: 'https://f72bf86b3fd54184b6cb0302df45a950@o104170.ingest.sentry.io/5607062',
    environment: sentryEnv,
    integrations: [new Integrations.BrowserTracing(), new Replay()],
    // Tracing disabled on QA — transaction quota (100k/100k) is exhausted for
    // this billing period, causing 429s that also block error events.
    // Production keeps tracing enabled; revisit after Apr 25 quota reset.
    enableTracing: sentryEnv !== 'qa',

    // Session Replay: don't record general sessions, but capture all error replays.
    // At ~210 filtered error events/month, this fits within the 500 replay quota.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: [
      // Browser quirks
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      // Stale deploy / chunk loading
      'Failed to fetch dynamically imported module',
      'Loading chunk',
      "text/html' is not a valid JavaScript MIME type",
      // Network transient
      'Network Error',
      // fetch() could not reach the server: Chrome, Firefox and Safari wording (SYM-6211)
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      'timeout of 0ms exceeded',
      'AbortError',
      // Browser permission
      'NotAllowedError',
      // Expired sessions — root cause visible in API monitoring (sympl-api Sentry).
      // Frontend 401 is always a downstream symptom, never the root cause.
      'Response not successful: Received status code 401',
      // Browser extension noise
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'atomicFindClose',
      'fb_xd_fragment',
    ],

    beforeSend(event) {
      // Drop events originating from browser extensions
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      if (
        frames.some(
          (frame) =>
            frame.filename?.startsWith('chrome-extension://') ||
            frame.filename?.startsWith('moz-extension://')
        )
      ) {
        return null;
      }
      return event;
    },
  });
}

if (import.meta.env.PROD) {
  initIntercom();
  if ((window as Window).innerWidth < 640) hideIntercom();
}

dayjs.extend(relativeTime);

function App() {
  return (
    <ErrorBoundary
      FallbackComponent={() => <ErrorPage />}
      onError={(error, info) =>
        Sentry.captureException(error, {
          contexts: { react: { componentStack: info.componentStack } },
        })
      }
    >
      <PostHogProvider
        apiKey={import.meta.env.VITE_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_POSTHOG_PROXY_HOST,
          ui_host: import.meta.env.VITE_POSTHOG_HOST,
          // Shows ugly early access features modal: opt_in_site_apps: true,
          //
          // Local testing only. VITE_FORCE_FEATURE_FLAGS is a comma separated
          // list of flags to switch on by hand. A local database invents its
          // customer, PostHog has never seen it, so every flag evaluates false
          // and flag-gated screens cannot be reached at all. When the variable
          // is set the listed flags start on and nothing is fetched, so they
          // stay on; flags left out of the list are off. The variable is unset
          // everywhere except a developer's own machine, which leaves these
          // options exactly as they were.
          ...(import.meta.env.VITE_FORCE_FEATURE_FLAGS
            ? {
                bootstrap: {
                  featureFlags: Object.fromEntries(
                    String(import.meta.env.VITE_FORCE_FEATURE_FLAGS)
                      .split(',')
                      .map((flag) => [flag.trim(), true])
                  ),
                },
                advanced_disable_feature_flags: true,
              }
            : {}),
        }}
      >
        <AppProvider>
          <ApolloProvider client={client}>
            <UserProvider>
              <GlobalStyles />
              <BrowserRouter>
                <PendingSaveProvider>
                  <NavigationProvider>
                    <PermissionProvider>
                      <GetStartedProvider>
                        <ModalsProvider>
                          <AppRouter />
                          <div
                            tw="fixed flex gap-2 items-center top-[45%] right-4 text-sm text-white bg-indigo-500 py-2 pr-4 pl-3 rounded-t-lg cursor-pointer origin-right rotate-[270deg] hover:bg-indigo-400 transition-colors"
                            onClick={showIntercomHome}
                          >
                            <Sparkle
                              weight="fill"
                              width={16}
                              height={16}
                              color="white"
                            />
                            Feedback
                          </div>
                        </ModalsProvider>
                      </GetStartedProvider>
                    </PermissionProvider>
                  </NavigationProvider>
                </PendingSaveProvider>
              </BrowserRouter>
            </UserProvider>
          </ApolloProvider>
        </AppProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('No root element found');
}
const reactRoot = createRoot(root);
reactRoot.render(<App />);
